/**
 * =============================================================================
 * ROASTBREW - SHARED SIDEBAR COMPONENT
 * =============================================================================
 *
 * Centralized sidebar navigation used across all authenticated pages.
 * Rendered by AppLayout, receives all state and callbacks from App.jsx.
 *
 * FEATURES:
 * - Logo and branding (RB monogram)
 * - "New conversation" button
 * - Navigation links (Home, Library, Courses, Calendar, Grade Check, Drive)
 * - Conversation history (shown only on Home page)
 * - Tier badge with subscription management link
 * - User profile (clickable → /profile) with logout
 * - Responsive: Desktop (persistent), Tablet/Mobile (overlay with backdrop)
 *
 * =============================================================================
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { canUseFeature } from './config';
import BrandLockup, { LOCKUP_RATIO } from './BrandLockup';
import UserAvatar from './UserAvatar';


// =============================================================================
// LOGO COMPONENT
// =============================================================================

// The brand lockup (signed R beside RoastBrew.) from the November 2026 logo
// package, drawn inline by BrandLockup (no image request). The free tier is a
// light palette, so it gets the dark-ink tone; pro / pro_plus are dark and get
// the parchment tone.
//
// Sized to fill the header: it takes the full width its wrapper gives it
// (232px on desktop, less beside the mobile/tablet close button), capped at
// 44px tall. The cap is applied as a max WIDTH (44px x the artwork's ratio) so
// the drawing always fills its own box and stays flush left.
const LOGO_MAX_HEIGHT = 44;

const RoastBrewLogo = ({ isLightTheme }) => (
  <BrandLockup
    tone={isLightTheme ? 'light' : 'dark'}
    style={{ width: '100%', maxWidth: LOGO_MAX_HEIGHT * LOCKUP_RATIO, height: 'auto' }}
  />
);


// =============================================================================
// NAVIGATION ITEMS CONFIGURATION
// =============================================================================

const PRIMARY_ACTION = {
  id: 'new-conversation',
  label: 'New Search',
  icon: (color) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  ),
  path: '/',
};

const NAV_ITEMS = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 22V12h6v10" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'courses',
    label: 'Courses',
    path: '/courses',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'library',
    label: 'Library',
    path: '/library',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'playlists',
    feature: 'playlists',   // shown only to plans that can use it (config.js FEATURE_MIN_TIER)
    label: 'Playlists',
    path: '/playlist',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <path d="M3 6h12M3 12h12M3 18h7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 15.5l6 3.5-6 3.5v-7z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'tools',
    feature: 'tools',       // shown only to plans that can use it (config.js FEATURE_MIN_TIER)
    label: 'Tools',
    path: '/tools',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'calendar',
    comingSoon: true,   // hidden from the sidebar until the page is built
    label: 'Calendar',
    path: '/calendar',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'grade-check',
    comingSoon: true,   // hidden from the sidebar until the page is built
    label: 'Grade Check',
    path: '/grade-check',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'drive',
    comingSoon: true,   // hidden from the sidebar until the page is built
    label: 'Drive',
    path: '/drive',
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

// ---------------------------------------------------------------------------
// FEATURE VISIBILITY
// ---------------------------------------------------------------------------
// Calendar, Grade Check and Drive are placeholder pages ("Coming Soon" screens
// with no functionality), so they are hidden from navigation. This is the ONLY
// place to change that.
//
// TO SHIP ONE: delete its `comingSoon: true` line in NAV_ITEMS above. Nothing
// else needs touching — the routes in App.jsx already exist and stay registered
// on purpose, so an old bookmark or shared link still resolves to the placeholder
// instead of 404ing. Hiding the entrance is not the same as removing the room.
//
// Set SHOW_COMING_SOON to true to reveal them all again for local development.
const SHOW_COMING_SOON = false;

const VISIBLE_NAV_ITEMS = SHOW_COMING_SOON
  ? NAV_ITEMS
  : NAV_ITEMS.filter((item) => !item.comingSoon);


// =============================================================================
// SIDEBAR COMPONENT
// =============================================================================

export default function Sidebar({
  isOpen = true,
  onToggle = () => {},
  onClose = () => {},
  onLogout = () => {},
  isMobile = false,
  isTablet = false,
  isDesktop = true,
  showConversations = true,
}) {
  // ---------------------------------------------------------------------------
  // ROUTER HOOKS
  // ---------------------------------------------------------------------------

  const navigate = useNavigate();
  const location = useLocation();

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/library')) return 'library';
    if (path.startsWith('/courses')) return 'courses';
    if (path.startsWith('/playlist')) return 'playlists';   // /playlist and every page under it
    if (path.startsWith('/tools')) return 'tools';
    if (path.startsWith('/calendar')) return 'calendar';
    if (path.startsWith('/grade-check')) return 'grade-check';
    if (path.startsWith('/drive')) return 'drive';
    if (path.startsWith('/video')) return 'video';
    if (path.startsWith('/subscription')) return 'subscription';
    if (path.startsWith('/profile')) return 'profile';
    return 'home';
  };
  const currentPage = getCurrentPage();

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  const [hoveredConvo, setHoveredConvo] = useState(null);
  const [hoveredNav, setHoveredNav] = useState(null);

  // ---------------------------------------------------------------------------
  // MOBILE ACCORDION
  // ---------------------------------------------------------------------------
  // PHONE ONLY. On a 667px-tall phone the drawer's fixed regions needed ~670px
  // (header 64 + primary action 96 + nav 330 + section head 34 + upgrade 70 +
  // profile 76), so Recent Topics — which had flex:1 — was squeezed to nothing
  // and the nav list scrolled inside its own box. Two nested scroll areas
  // competing for one screen height is why neither showed usable content.
  //
  // Tablets keep the old layout: this app's tablet breakpoint (768-1023px wide)
  // is always >=1024px TALL in practice, so there is no squeeze to fix there.
  //
  // Strict accordion: exactly one section open, or none.
  const [openSection, setOpenSection] = useState(null);   // 'nav' | 'recent' | null
  const toggleSection = (key) => setOpenSection((cur) => (cur === key ? null : key));

  // Both sections start collapsed. The one concession: when you arrive on a new
  // page, pre-open the section that page lives in, so the drawer is not two
  // headers and nothing else. Delete this effect for a pure collapsed-on-open.
  const lastPageRef = useRef(null);

  // Pre-open the section the current page belongs to, once per page change.
  useEffect(() => {
    if (!isMobile) return;
    if (lastPageRef.current === currentPage) return;
    lastPageRef.current = currentPage;
    setOpenSection(currentPage === 'video' ? 'recent' : 'nav');
  }, [currentPage, isMobile]);

  // Reset to fully collapsed each time the drawer is dismissed, so reopening is
  // always the same predictable state rather than wherever you left it.
  useEffect(() => {
    if (!isMobile || isOpen) return;
    setOpenSection(null);
  }, [isOpen, isMobile]);

  // ---------------------------------------------------------------------------
  // COMPUTED VALUES
  // ---------------------------------------------------------------------------

  const { colors, isLight: isLightTheme } = useTheme();
  const { profile, algorithm } = useAuth();
  const user = profile ? { name: profile.name, email: profile.email } : null;
  // Tabs for gated pages (Playlists, Tools) appear only for plans that can use them.
  const navItems = VISIBLE_NAV_ITEMS.filter(
    (item) => !item.feature || canUseFeature(item.feature, profile?.currentTier || 'free')
  );
  // ── Recent Topics entrance schedule ──────────────────────────────────────
  // Gaps decay geometrically rather than sitting on a fixed cadence, because a
  // constant step is the thing that reads as mechanical.
  //
  //   gaps: 340, 286, 240, 202, 169, 142, 119 ...
  //
  // Two properties make this better than easing the schedule with a bezier:
  //
  //   Every gap stays above ~119ms, clear of the ~80ms floor where two events
  //   start reading as one. A bezier normalised over a fixed window has to
  //   reach its endpoint, so its tail always collapses into a clump — the
  //   house curve, for instance, ends on gaps of 49ms and 14ms.
  //
  //   The series converges (340 / (1 - 0.84) ≈ 2.1s), so the cascade is
  //   self-bounding however long the list is. No arbitrary cap needed.
  //
  // The first gap is 340ms — half the 0.7s item duration — so the top of the
  // list keeps the halfway overlap and the rest tightens into a flourish.
  const STAGGER_FIRST = 340;
  const STAGGER_DECAY = 0.84;
  const staggerDelay = (index) =>
    Math.round(
      (STAGGER_FIRST * (1 - Math.pow(STAGGER_DECAY, index))) / (1 - STAGGER_DECAY)
    );

  // Defensive: recentlyWatched can be absent, null, or (mid-fetch) not yet an
  // array. Anything that is not a real array is treated as empty so the section
  // renders its empty state rather than throwing on .map().
  const recentTopics = Array.isArray(algorithm?.recentlyWatched)
    ? algorithm.recentlyWatched
    : [];
  const borderColor = isLightTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  const subtleBg = isLightTheme ? 'rgba(26,26,26,0.04)' : 'rgba(255,255,255,0.04)';
  const subtleBgHover = isLightTheme ? 'rgba(26,26,26,0.08)' : 'rgba(255,255,255,0.08)';
  const subtleBorder = isLightTheme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------

  // Accordion header, styled to match the primary action button above it:
  // same 14px sentence-case label, same 10px radius, same subtleBg fill and
  // 1px border, same 12px icon gap. The previous version used an 11px uppercase
  // letterspaced label with no icon and no border, which read as a bare list
  // caption dropped into a column of cards — visually it did not belong.
  const sectionHeader = (key, label, icon) => {
    const open = openSection === key;
    const hot = hoveredNav === 'section-' + key;
    return (
      <button
        onClick={() => toggleSection(key)}
        onMouseEnter={() => setHoveredNav('section-' + key)}
        onMouseLeave={() => setHoveredNav(null)}
        style={{
          width: '100%', padding: '14px 16px',
          display: 'flex', alignItems: 'center', gap: '12px',
          background: open || hot ? subtleBgHover : subtleBg,
          border: `1px solid ${open ? `${colors.accent}50` : (hot ? colors.accent : subtleBorder)}`,
          borderRadius: '10px', color: colors.text,
          fontSize: '14px', fontFamily: '"DM Sans", sans-serif',
          cursor: 'pointer', transition: 'all 0.2s ease', textAlign: 'left',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {icon(open ? colors.accent : 'currentColor')}
        <span style={{ flex: 1 }}>{label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={open ? colors.accent : colors.muted} strokeWidth="2"
          style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.25s ease' }}>
          <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  };

  const iconMenu = (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" style={{ flexShrink: 0 }}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
  const iconRecent = (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const handlePrimaryAction = () => {
    navigate(PRIMARY_ACTION.path);
    if (isMobile || isTablet) onClose();
  };

  const handleNavClick = (item) => {
    navigate(item.path);
    if (isMobile || isTablet) onClose();
  };

  const handleSubscriptionClick = () => {
    navigate('/subscription');
    if (isMobile || isTablet) onClose();
  };

  const handleProfileClick = () => {
    navigate('/profile');
    if (isMobile || isTablet) onClose();
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* WHY Sign Out WAS CLIPPED.
          The container was height:100vh. On iOS, 100vh is the height of the
          viewport with the browser chrome COLLAPSED — it is taller than what you
          can actually see while the URL bar is showing, so the bottom of the
          drawer was pushed below the fold and the last control got cut off.
          100dvh tracks the live visible height. The duplicate declaration is a
          deliberate cascade fallback: browsers without dvh keep the vh value. */}
      <style>{`
        .rb-sidebar { height: 100vh; height: 100dvh; }

        /* Recent Topics entrance.

           The list arrives asynchronously, so without this the items pop in as
           a block the moment the fetch resolves. Three things make them read as
           deliberate instead:

           1. The house curve and duration. cubic-bezier(0.4, 0, 0.2, 1) at 0.7s
              is what fadeInUp uses on HomePage, ManageSubscription and AuthUI,
              and translateY(20px) is the same distance.

           2. A 350ms step, which is exactly half the duration, so each item
              begins as the one above it reaches the midpoint. The cascade never
              breaks into separate events.

           3. The text inks in from the sidebar surface colour rather than
              fading opacity. Because the glyphs start the same colour as what
              is behind them, they appear to surface out of the panel rather
              than to be layered on top of it.

           --rb-delay and the colour custom properties are set inline per item;
           CSS variables inherit, so the spans pick up the parent's delay and
           stay in step with the rise. */
        @keyframes rbRecentRise {
          from { transform: translateY(20px); }
          to   { transform: translateY(0); }
        }
        @keyframes rbRecentInkTitle {
          from { color: var(--rb-surface); }
          to   { color: var(--rb-title); }
        }
        @keyframes rbRecentInkSub {
          from { color: var(--rb-surface); }
          to   { color: var(--rb-sub); }
        }

        .rb-recent-item {
          animation: rbRecentRise 0.7s cubic-bezier(0.4, 0, 0.2, 1) var(--rb-delay, 0ms) both;
        }
        .rb-recent-item .rb-recent-title {
          animation: rbRecentInkTitle 0.7s cubic-bezier(0.4, 0, 0.2, 1) var(--rb-delay, 0ms) both;
        }
        .rb-recent-item .rb-recent-sub {
          animation: rbRecentInkSub 0.7s cubic-bezier(0.4, 0, 0.2, 1) var(--rb-delay, 0ms) both;
        }
        .rb-recent-empty {
          animation:
            rbRecentRise 0.7s cubic-bezier(0.4, 0, 0.2, 1) both,
            rbRecentInkSub 0.7s cubic-bezier(0.4, 0, 0.2, 1) both;
        }

        /* An animated list that appears after a fetch is exactly the motion
           pattern people disable this for. */
        @media (prefers-reduced-motion: reduce) {
          .rb-recent-item,
          .rb-recent-item .rb-recent-title,
          .rb-recent-item .rb-recent-sub,
          .rb-recent-empty { animation: none; }
        }
      `}</style>

      {/* Mobile/Tablet Backdrop */}
      {(isMobile || isTablet) && isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 998,
            transition: 'opacity 0.3s ease',
          }}
        />
      )}

      {/* Sidebar Container */}
      <aside
        className="rb-sidebar"
        style={{
          // Always 280 px wide: the whole sidebar slides in and out as one piece, like the Course
          // Navigator, so its text stays frozen in place. (It used to narrow to 0, which squeezed and
          // re-wrapped every label on the way.)
          width: '280px',
          minWidth: '280px',
          background: colors.sidebar,
          borderRight: `1px solid ${borderColor}`,
          display: 'flex', flexDirection: 'column',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden', position: 'fixed',
          left: 0,
          transform: isOpen ? 'none' : 'translateX(-100%)',
          zIndex: 999,
          boxShadow: (isMobile || isTablet) && isOpen ? '4px 0 20px rgba(0,0,0,0.3)' : 'none',
        }}
      >
        {/* =============================================================
            SIDEBAR HEADER — Logo + Mobile Close
            ============================================================= */}
        <div style={{ padding: '24px', borderBottom: `1px solid ${borderColor}` }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '24px',
          }}>
            <div onClick={handlePrimaryAction} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minHeight: '40px', flex: 1, minWidth: 0, marginRight: '12px' }}>
              <RoastBrewLogo isLightTheme={isLightTheme} />
            </div>

            {(isMobile || isTablet) && (
              <button
                onClick={onClose}
                style={{
                  width: '36px', height: '36px', borderRadius: '8px',
                  background: subtleBg, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: colors.muted, transition: 'all 0.2s ease',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Primary Action */}
          <button
            onClick={handlePrimaryAction}
            onMouseEnter={() => setHoveredNav('primary-action')}
            onMouseLeave={() => setHoveredNav(null)}
            style={{
              width: '100%', padding: '14px 16px',
              display: 'flex', alignItems: 'center', gap: '12px',
              background: hoveredNav === 'primary-action' ? subtleBgHover : subtleBg,
              border: `1px solid ${hoveredNav === 'primary-action' ? colors.accent : subtleBorder}`,
              borderRadius: '10px', color: colors.text,
              fontSize: '14px', fontFamily: '"DM Sans", sans-serif',
              cursor: 'pointer', transition: 'all 0.2s ease',
            }}
          >
            {PRIMARY_ACTION.icon('currentColor')}
            {PRIMARY_ACTION.label}
          </button>
        </div>

        {/* =============================================================
            SCROLL BODY (phones)
            =============================================================
            THE ACTUAL FIX. The drawer used to have TWO independently scrolling
            regions — the nav list (overflowY:auto) and Recent Topics (flex:1) —
            inside one fixed-height column. On a phone the fixed chrome already
            exceeded the viewport, so flex:1 resolved to ~0 and Recent Topics
            vanished while the nav scrolled in a cramped box.
            One scroll container, header pinned above, profile pinned below.
            ============================================================= */}
        <div style={isMobile
          ? { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
          : { display: 'contents' }}>

        {/* =============================================================
            NAVIGATION
            ============================================================= */}
        {/* On PHONES this whole region lives inside one shared scroll container
            and both sections are accordions. On tablet and desktop the original
            always-open layout is unchanged. */}
        <div style={isMobile ? {
          padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '4px',
        } : {
          padding: '16px 24px', borderBottom: `1px solid ${borderColor}`,
          display: 'flex', flexDirection: 'column', gap: '4px',
          overflowY: 'auto',
        }}>
          {isMobile && sectionHeader('nav', 'Menu', iconMenu)}
          {(!isMobile || openSection === 'nav') && navItems.map((item) => {
            const isActive = currentPage === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                onMouseEnter={() => !isActive && setHoveredNav(item.id)}
                onMouseLeave={() => setHoveredNav(null)}
                style={{
                  width: '100%', padding: '12px 16px',
                  display: 'flex', alignItems: 'center', gap: '12px',
                  background: isActive
                    ? (isLightTheme ? 'rgba(139,139,122,0.1)' : `${colors.accent}15`)
                    : (hoveredNav === item.id ? subtleBgHover : 'transparent'),
                  border: `1px solid ${isActive ? `${colors.accent}50` : (hoveredNav === item.id ? subtleBorder : 'transparent')}`,
                  borderRadius: '10px', color: colors.text,
                  fontSize: '14px', fontFamily: '"DM Sans", sans-serif',
                  cursor: isActive ? 'default' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {item.icon(isActive ? colors.accent : 'currentColor')}
                {item.label}
              </button>
            );
          })}
        </div>

        {/* =============================================================
            RECENT TOPICS (all pages)
            ============================================================= */}
        {showConversations ? (
          <div style={isMobile
            ? { padding: '0 12px 8px' }
            : { flex: 1, overflowY: 'auto', padding: '12px' }}>
            {isMobile ? sectionHeader('recent', 'Recent Topics', iconRecent) : (
              <p style={{
                fontSize: '11px', fontWeight: '600', letterSpacing: '0.1em',
                textTransform: 'uppercase', color: colors.muted,
                padding: '8px 8px 12px', fontFamily: '"DM Sans", sans-serif',
              }}>
                Recent Topics
              </p>
            )}
            {(!isMobile || openSection === 'recent') && recentTopics.length === 0 && (
              <p className="rb-recent-empty" style={{
                margin: 0, padding: '4px 8px 12px', fontSize: '13px', lineHeight: 1.6,
                color: colors.muted, fontFamily: '"DM Sans", sans-serif',
                '--rb-surface': colors.sidebar,
                '--rb-sub': colors.muted,
              }}>
                Your most recently watched videos will appear here once you begin
                watching.
              </p>
            )}
            {(!isMobile || openSection === 'recent') && recentTopics.map((topic, index) => (
              <button
                key={topic.videoId}
                className="rb-recent-item"
                onClick={() => { navigate('/video/' + topic.videoId); if (isMobile || isTablet) onClose(); }}
                onMouseEnter={() => setHoveredConvo(topic.videoId)}
                onMouseLeave={() => setHoveredConvo(null)}
                style={{
                  width: '100%', padding: '12px', display: 'flex',
                  flexDirection: 'column', alignItems: 'flex-start', gap: '4px',
                  background: hoveredConvo === topic.videoId ? subtleBg : 'transparent',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  transition: 'background 0.2s ease', textAlign: 'left',

                  '--rb-delay': `${staggerDelay(index)}ms`,

                  // The glyphs begin at the panel colour and resolve to their
                  // own, so the text surfaces out of the sidebar instead of
                  // fading in on top of it.
                  '--rb-surface': colors.sidebar,
                  '--rb-title': colors.text,
                  '--rb-sub': colors.muted,
                }}
              >
                <span className="rb-recent-title" style={{
                  fontSize: '14px', color: colors.text,
                  fontFamily: '"DM Sans", sans-serif', fontWeight: '400',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%',
                }}>
                  {topic.title}
                </span>
                <span className="rb-recent-sub" style={{
                  fontSize: '12px', color: colors.muted, fontFamily: '"DM Sans", sans-serif',
                }}>
                  {topic.series}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div style={isMobile ? {} : { flex: 1 }} />
        )}

        </div>

        {/* =============================================================
            SIDEBAR FOOTER — Tier badge + User profile + Logout
            ============================================================= */}
        {/* flexShrink:0 so the footer can never be compressed by the scroll body
            above it, and a safe-area pad so Sign Out clears the home indicator. */}
        <div style={{
          padding: '16px 24px calc(16px + env(safe-area-inset-bottom))',
          borderTop: `1px solid ${borderColor}`,
          flexShrink: 0,
        }}>
          {/* Tier Badge */}
          <button
            onClick={handleSubscriptionClick}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.accent}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = `${colors.accent}30`}
            style={{
              width: '100%', padding: '12px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: isLightTheme ? 'rgba(26,26,26,0.03)' : `${colors.accent}10`,
              border: `1px solid ${colors.accent}30`, borderRadius: '10px',
              cursor: 'pointer', marginBottom: '16px', transition: 'all 0.2s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: colors.accent,
                boxShadow: !isLightTheme ? `0 0 12px ${colors.accent}` : 'none',
              }} />
              <span style={{
                fontSize: '13px', fontWeight: '600', color: colors.text,
                fontFamily: '"DM Sans", sans-serif',
              }}>
                {colors.label} Plan
              </span>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.muted} strokeWidth="1.5">
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* User Profile Row — clickable to /profile */}
          <button
            onClick={handleProfileClick}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '14px',
              padding: '8px 4px', background: 'none', border: 'none',
              cursor: 'pointer', borderRadius: '8px',
              transition: 'background 0.2s ease', textAlign: 'left',
              marginBottom: '12px',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = subtleBg}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          >
            {/* Initial avatar from avatars.js (dark artwork on every tier). On the
                dark tiers a gold hairline keeps the ink circle from melting into
                the ink sidebar. */}
            <UserAvatar
              name={user.name}
              size={40}
              style={{
                boxShadow: isLightTheme
                  ? '0 1px 3px rgba(0,0,0,0.18)'
                  : `0 0 0 1px ${colors.accent}55, 0 4px 16px ${colors.accent}30`,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontSize: '14px', fontWeight: '500', color: colors.text,
                fontFamily: '"DM Sans", sans-serif', margin: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {user.name}
              </p>
              <p style={{
                fontSize: '12px', color: colors.muted,
                fontFamily: '"DM Sans", sans-serif', margin: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {user.email}
              </p>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.muted} strokeWidth="1.5" style={{ flexShrink: 0 }}>
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Logout Button */}
          <button
            onClick={onLogout}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = subtleBgHover;
              e.currentTarget.style.color = colors.text;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = colors.muted;
            }}
            style={{
              width: '100%', padding: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.muted, borderRadius: '8px',
              transition: 'all 0.2s ease',
              fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
